<template>
  <div class="z-pb-1200">
    <YContainer>
      <z-breadcrumb
        :items="[
          { text: 'Home', url: 'https://querobolsa.com.br/' },
          { text: 'Manual do Enem', url: '/enem/manual-do-enem' },
          { text: category.name },
        ]"
      />
      <MontillaSlot
        slug="enem-category-banner"
        :tags="slotTags"
      />

      <div class="category__header">
        <div
          class="category__header-icon-wrapper"
          :style="{ backgroundColor: category.color }"
        >
          <z-icon
            :icon="['fal', categorySlugIconMap[category.slug]]"
            color="inverted"
            size="extra-giant"
          />
        </div>

        <z-title tag="h1">
          {{ category.name }}
        </z-title>
      </div>

      <z-title
        tag="h2"
        size="extra-small"
      >
        Aulas sobre {{ category.name }}
      </z-title>

      <z-text tag="p">
        Selecionamos os conteúdos essenciais para você arrasar em todos os vestibulares.
      </z-text>

      <z-text tag="p">
        Bons estudos! <span
          role="img"
          aria-label="Emoji com um leve sorriso"
        >🙂</span>
      </z-text>

      <div class="category__lessons">
        <z-card
          v-for="lesson in lessons"
          :key="lesson.slug"
          shadow="high"
          class="category__lessons-card"
        >
          <z-link
            :href="`/enem/${$route.params.category}/${lesson.slug}`"
            class="category__lessons-link"
          >
            <VLazyImage
              class="category__lessons-cover"
              :src="imageSource(lesson.cover_image)"
              :alt="`Foto ilustrativa sobre o assunto de ${lesson.title}`"
            />

            <div class="category__lessons-content">
              <z-badge-text
                variant="info"
                :value="category.name"
              />

              <z-text
                weight="medium"
                class="category__lessons-title"
              >
                {{ lesson.title }}
              </z-text>
            </div>
          </z-link>
        </z-card>
      </div>
    </YContainer>
  </div>
</template>

<script>
import VLazyImage from 'v-lazy-image/v2'
import { YContainer } from '@quero/yeti'

import { CATEGORY_ICON_MAP } from '~/assets/js/services/categories'
import { getLessonsByCategory } from '~/assets/js/services/graphqlApi'
import { retry } from '~/assets/js/utils/http'
import MontillaSlot from '~/components/montilla-slot'
import { categorySlotTags } from '~/utils/montilla-slot-tags'

export default {
  name: 'CategoryIndex',
  components: {
    YContainer,
    VLazyImage,
    MontillaSlot,
  },
  async asyncData ({ $cms, error, params }) {
    try {
      const query = getLessonsByCategory(params.category)
      const graphqlQuery = {
        query,
      }
      // const lessons = await $cms.$post('/graphql', graphqlQuery).then((response) => {
      //   return response.data.ManualDoEnemAulas
      // })
      const lessons = await retry(
        () => $cms.$post('/graphql', graphqlQuery),
        { retries: 6 },
      ).then((response) => {
        return response.data.ManualDoEnemAulas
      })

      if (!lessons.length) {
        return error({
          statusCode: 404,
          message: `Page not found for category ${params.category}`,
        })
      }

      const category = lessons[0].category

      return {
        lessons,
        category,
      }
    } catch (err) {
      error({
        statusCode: 500,
        message: `Failed to fetch lessons for category ${params.category}`,
      })
    }
  },
  data () {
    return {
      lessons: [],
      category: {},
      categorySlugIconMap: CATEGORY_ICON_MAP,
    }
  },
  head () {
    return {
      title: `${this.category.name} - Manual do Enem`,
      meta: [
        { hid: 'description', name: 'description', content: 'Aqui no Manual do Enem, você encontra conteúdos, videos e exercícios relacionados ao Enem e Vestibulares. Aprenda Matemática, Redação, Biologia, Português, etc.' },
      ],
      script: [{
        src: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.5/MathJax.js?config=TeX-MML-AM_CHTML',
        async: true,
        defer: true,
      }],
    }
  },
  computed: {
    slotTags () {
      return categorySlotTags(this.category)
    },
  },
  mounted () {
    window.montilla.track('category_view', { content_view_name: 'enem-categoria' })
  },
  methods: {
    imageSource (coverImage) {
      return this.$mediaHub(coverImage)
    },
  },
}
</script>

<style lang="scss">
.category__header {
  align-items: center;
  display: flex;
}

.category__header-icon-wrapper {
  align-items: center;
  border-radius: var(--border-radius-circle);
  display: flex;
  height: var(--unit-1700);
  justify-content: center;
  margin-right: var(--unit-0400);
  width: var(--unit-1700);
}

.category__lessons-link {
  --color-interactive-hover: var(--color-gray-700);
  --color-interactive-normal: var(--color-gray-500);

  width: 100%;
}

.category__lessons-card {
  padding: 0;
  overflow: hidden;
}

.category__lessons-content {
  padding: var(--unit-0400);
}

.category__lessons-title {
  display: block;
  margin-top: var(--unit-0400);
}

.category__lessons {
  display: grid;
  gap: var(--unit-0600);
  margin-top: var(--unit-1200);

  @media (min-width: $breakpoint-tablet) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.category__lessons-cover {
  height: 120px;
  object-fit: cover;
  width: 100%;

  @media (min-width: $breakpoint-tablet) {
    height: 200px;
  }
}

.v-lazy-image {
  width: 100%;
}
</style>
